import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getCodiciOmaggioDaStampare, getImpostazioniOmaggio, saveImpostazioniOmaggio } from '../services/api';

const ETICHETTE_TIPO = { PRIMO_ACQUISTO: 'Primo acquisto', SPEDIZIONE_GRATUITA: 'Spedizione gratuita' };

// Riepilogo in Ordini dei codici omaggio (ordini IBS/Feltrinelli) ancora da stampare, con impostazioni rapide.
function CodiciOmaggioBox() {
  const navigate = useNavigate();
  const [codici, setCodici] = useState([]);
  const [selezionati, setSelezionati] = useState([]);
  const [aperto, setAperto] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [percentuale, setPercentuale] = useState('');
  const [giorni, setGiorni] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [mostraImpostazioni, setMostraImpostazioni] = useState(false);

  const carica = useCallback(async () => {
    try {
      const [codiciRes, cfgRes] = await Promise.all([getCodiciOmaggioDaStampare(), getImpostazioniOmaggio()]);
      setCodici(codiciRes.data || []);
      setSelezionati(prev => prev.filter(id => (codiciRes.data || []).some(c => c.id === id)));
      setCfg(cfgRes.data);
      setPercentuale(String(cfgRes.data.percentuale));
      setGiorni(String(cfgRes.data.giorni_validita));
    } catch (e) {
      console.error('Errore caricamento codici omaggio:', e);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const salva = async (extra = {}) => {
    setSalvataggio(true);
    try {
      const res = await saveImpostazioniOmaggio({
        percentuale: parseFloat(percentuale.replace(',', '.')),
        giorni_validita: parseInt(giorni, 10),
        ...extra,
      });
      setCfg(res.data);
    } catch (e) {
      alert(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Impostazioni non valide');
    } finally {
      setSalvataggio(false);
    }
  };

  const stampa = (ids) => navigate(`/codici-sconto/omaggio/print${ids ? `?ids=${ids.join(',')}` : ''}`);
  const toggle = (id) => setSelezionati(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  if (!cfg) return null;
  if (codici.length === 0 && !mostraImpostazioni) {
    return (
      <div className="mb-2 text-end small">
        <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setMostraImpostazioni(true)}>
          Codici omaggio ordini marketplace: nessun biglietto da stampare · impostazioni
        </Button>
      </div>
    );
  }

  return (
    <Card className="mb-3 border-primary">
      <Card.Header className="d-flex justify-content-between align-items-center py-2" style={{ cursor: 'pointer' }}
        onClick={() => setAperto(a => !a)}>
        <span>
          <strong>Codici omaggio da stampare</strong>{' '}
          <Badge bg={codici.length ? 'primary' : 'secondary'}>{codici.length}</Badge>
          <span className="text-muted small ms-2">biglietti da infilare nel pacco (2 per foglio A4)</span>
        </span>
        <span>{aperto ? '▾' : '▸'}</span>
      </Card.Header>
      {aperto && (
        <Card.Body className="py-2">
          {codici.length > 0 && (
            <>
              <div className="mb-2 d-flex gap-2 flex-wrap">
                <Button size="sm" variant="primary" onClick={() => stampa(null)}>
                  Stampa tutti ({codici.length})
                </Button>
                <Button size="sm" variant="outline-primary" disabled={selezionati.length === 0} onClick={() => stampa(selezionati)}>
                  Stampa selezionati ({selezionati.length})
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setMostraImpostazioni(m => !m)}>
                  Impostazioni
                </Button>
              </div>
              <Table size="sm" hover responsive className="mb-1">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>
                      <Form.Check checked={selezionati.length === codici.length}
                        onChange={e => setSelezionati(e.target.checked ? codici.map(c => c.id) : [])} />
                    </th>
                    <th>Codice</th><th>Tipo</th><th>Cliente</th><th>Ordine</th><th>Scade</th>
                  </tr>
                </thead>
                <tbody>
                  {codici.map(c => (
                    <tr key={c.id}>
                      <td><Form.Check checked={selezionati.includes(c.id)} onChange={() => toggle(c.id)} /></td>
                      <td><code>{c.codice}</code></td>
                      <td>
                        {ETICHETTE_TIPO[c.tipo] || c.tipo}
                        {c.tipo === 'PRIMO_ACQUISTO' && <> · {c.percentuale_sconto}%</>}
                      </td>
                      <td>{c.cliente_nome || '—'}</td>
                      <td>
                        {c.ordine_id ? <a href={`/ordini/${c.ordine_id}`}>#{c.ordine_id}</a> : '—'}
                        {c.origine && <span className="text-muted small"> {c.origine}</span>}
                      </td>
                      <td>{c.valido_fino ? new Date(c.valido_fino).toLocaleDateString('it-IT') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
          {mostraImpostazioni && (
            <div className="border-top pt-2 mt-2 d-flex gap-3 align-items-end flex-wrap">
              <Form.Group>
                <Form.Label className="small mb-0">Sconto primo acquisto (%)</Form.Label>
                <Form.Control size="sm" style={{ width: 110 }} value={percentuale} onChange={e => setPercentuale(e.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label className="small mb-0">Validità (giorni)</Form.Label>
                <Form.Control size="sm" style={{ width: 110 }} value={giorni} onChange={e => setGiorni(e.target.value)} />
              </Form.Group>
              <Button size="sm" onClick={() => salva()} disabled={salvataggio}>
                {salvataggio ? <Spinner animation="border" size="sm" /> : 'Salva'}
              </Button>
              <Form.Check type="switch" label="Genera codici automaticamente" checked={cfg.attivi}
                onChange={e => salva({ attivi: e.target.checked })} />
            </div>
          )}
        </Card.Body>
      )}
    </Card>
  );
}

export default CodiciOmaggioBox;
